import { supabase } from '../supabase';
import type {
  BatchItem, Card, CardVibe, InFlight, LeaderRow, Member, Note, NoteKind, OpenThread, Row,
} from '../types';

export type Queue = 'flagged' | 'unread' | 'reviewed';

/** Rows for one queue in one locale. Sorted by the source's own order so a
 *  reviewer walks the deck the way a player would meet it. */
export async function fetchRows(
  locales: string[], queue: Queue, opts: { kind?: string; limit?: number; offset?: number } = {},
): Promise<Row[]> {
  let q = supabase
    .from('i18n_review_row')
    .select('*')
    .in('locale', locales)
    .order('sort_key', { ascending: true })
    .range(opts.offset ?? 0, (opts.offset ?? 0) + (opts.limit ?? 40) - 1);

  if (opts.kind) q = q.eq('kind', opts.kind);
  if (queue === 'flagged') q = q.neq('flags', '{}');
  if (queue === 'unread') q = q.eq('flags', '{}').eq('state', 'machine').eq('aligned', true);
  if (queue === 'reviewed') q = q.eq('state', 'reviewed');

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Row[];
}

/**
 * Rows for a queue, grouped into cards.
 *
 * Two queries on purpose. The first finds what matches the filter; the second
 * pulls in the other half of any deck prompt it hit, because an instruction can
 * be flagged while its question is clean, and showing one without the other is
 * how you get a reviewer guessing.
 */
export async function fetchCards(
  locales: string[], queue: Queue, opts: { limit?: number; offset?: number } = {},
): Promise<Card[]> {
  return (await fetchCardsPage(locales, queue, opts)).cards;
}

/**
 * Same as fetchCards, but also says whether the queue has run dry.
 *
 * The unread queue drops cards after the fetch, so a short page there does not
 * mean the end; only a short set of matched rows does. Focus mode walks a
 * queue to its end and needs the distinction, the paged list does not.
 */
export async function fetchCardsPage(
  locales: string[], queue: Queue, opts: { kind?: string; limit?: number; offset?: number } = {},
): Promise<{ cards: Card[]; exhausted: boolean }> {
  const matched = await fetchRows(locales, queue, opts);
  const exhausted = matched.length < (opts.limit ?? 40);
  if (!matched.length) return { cards: [], exhausted: true };

  const promptIds = [...new Set(matched.filter((r) => r.kind === 'deck' && r.prompt_id)
    .map((r) => r.prompt_id as string))];

  let siblings: Row[] = [];
  if (promptIds.length) {
    const { data, error } = await supabase
      .from('i18n_review_row').select('*')
      .in('prompt_id', promptIds).in('locale', locales);
    if (error) throw error;
    siblings = (data ?? []) as Row[];
  }

  const byKey = new Map<string, Card>();
  const keyOf = (r: Row) =>
    r.kind === 'deck' && r.prompt_id ? `${r.prompt_id}@${r.locale}` : `${r.unit_id}@${r.locale}`;

  // Seed from the matches so card order follows the queue, not the sibling fetch.
  for (const r of matched) {
    const k = keyOf(r);
    if (!byKey.has(k)) {
      byKey.set(k, {
        key: k, kind: r.kind, deck: r.deck, prompt_id: r.prompt_id, locale: r.locale, parts: [],
      });
    }
  }
  const all = [...matched, ...siblings];
  for (const r of all) {
    const card = byKey.get(keyOf(r));
    if (card && !card.parts.some((p) => p.unit_id === r.unit_id)) card.parts.push(r);
  }
  // Question before instruction, whatever order they arrived in.
  for (const card of byKey.values()) {
    card.parts.sort((a, b) => (a.field === 'template' ? -1 : b.field === 'template' ? 1 : 0));
  }
  // Attach anything already under way, so nobody redoes another reviewer's
  // work. Cheap: one call for the whole page.
  const { data: flight } = await supabase.rpc('i18n_inflight', { p_locales: locales });
  const flightBy = new Map<string, InFlight>(
    ((flight ?? []) as InFlight[]).map((f) => [`${f.unit_id}@${f.locale}`, f]));

  let cards = [...byKey.values()];
  for (const card of cards) {
    for (const p of card.parts) {
      const f = flightBy.get(`${p.unit_id}@${p.locale}`);
      if (f) { card.inflight = f; break; }
    }
  }

  // The conversation, in the same one round trip. Threads are rare enough that
  // fetching them per card on expand would be more requests than it saves, and
  // a reviewer has to see there IS a question before deciding to open it.
  const notes = await fetchNotes(all.map((r) => r.unit_id), locales);
  if (notes.length) {
    const byUnit = new Map<string, Note[]>();
    for (const n of notes) {
      const k = `${n.unit_id}@${n.locale}`;
      (byUnit.get(k) ?? byUnit.set(k, []).get(k)!).push(n);
    }
    for (const card of cards) {
      const thread = card.parts.flatMap((p) => byUnit.get(`${p.unit_id}@${p.locale}`) ?? []);
      if (thread.length) {
        card.notes = thread.sort((a, b) => a.created_at.localeCompare(b.created_at));
      }
    }
  }
  // Matches how the counts are computed: a prompt whose instruction is flagged
  // belongs in Known problems, even if the half that matched here was clean.
  if (queue === 'unread') cards = cards.filter((c) => !c.parts.some((p) => p.flags.length));

  // Ratings last, over what actually survived the filter.
  const rated = [...new Set(cards.filter((c) => c.prompt_id).map((c) => c.prompt_id as string))];
  if (rated.length) {
    const vibes = await fetchVibes(rated, locales);
    const vibeBy = new Map(vibes.map((v) => [`${v.prompt_id}@${v.locale}`, v]));
    for (const card of cards) {
      if (card.prompt_id) card.vibe = vibeBy.get(`${card.prompt_id}@${card.locale}`);
    }
  }
  return { cards, exhausted };
}

/**
 * Queue sizes as cards, not rows — what a reviewer actually pages through.
 * One round trip for all three, and the database makes the flagged and unread
 * queues disjoint so a prompt never appears in both.
 */
export async function queueCounts(locales: string[]): Promise<Record<string, number>> {
  if (!locales.length) return {};
  const { data, error } = await supabase.rpc('i18n_queue_counts', { p_locales: locales });
  if (error) throw error;
  return Object.fromEntries(
    ((data ?? []) as { queue: string; cards: number }[]).map((r) => [r.queue, Number(r.cards)]),
  );
}

/** The member's open cart, created on first use. */
export async function getOrCreateDraft(memberId: string): Promise<string> {
  const { data: existing } = await supabase
    .from('i18n_batch').select('id')
    .eq('member_id', memberId).eq('state', 'draft')
    .order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (existing?.id) return existing.id as string;

  const { data, error } = await supabase
    .from('i18n_batch').insert({ member_id: memberId }).select('id').single();
  if (error) throw error;
  return data.id as string;
}

export async function loadDraftItems(batchId: string): Promise<BatchItem[]> {
  const { data, error } = await supabase
    .from('i18n_batch_item').select('*').eq('batch_id', batchId);
  if (error) throw error;
  return (data ?? []) as BatchItem[];
}

export async function putItem(batchId: string, item: BatchItem): Promise<BatchItem> {
  const { data, error } = await supabase
    .from('i18n_batch_item')
    .upsert({ ...item, batch_id: batchId }, { onConflict: 'batch_id,unit_id,locale' })
    .select('*').single();
  if (error) throw error;
  return data as BatchItem;
}

export async function dropItem(batchId: string, unitId: string, locale: string) {
  const { error } = await supabase.from('i18n_batch_item').delete()
    .eq('batch_id', batchId).eq('unit_id', unitId).eq('locale', locale);
  if (error) throw error;
}

export async function submitBatch(batchId: string, note: string | null) {
  const { error } = await supabase.from('i18n_batch')
    .update({ state: 'submitted', note, submitted_at: new Date().toISOString() })
    .eq('id', batchId);
  if (error) throw error;
}

/* ---------------------------------------------------------------- owner */

export async function fetchMembers(): Promise<Member[]> {
  const { data, error } = await supabase
    .from('i18n_member').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Member[];
}

export async function setMemberStatus(
  id: string, patch: Partial<Pick<Member, 'status' | 'role' | 'languages' | 'ai_editor'>>,
) {
  const { error } = await supabase.from('i18n_member')
    .update({ ...patch, approved_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

export interface SubmittedBatch {
  id: string;
  member_id: string;
  note: string | null;
  submitted_at: string;
  member: { display_name: string; avatar_url: string | null } | null;
  items: BatchItem[];
}

export async function fetchSubmitted(): Promise<SubmittedBatch[]> {
  const { data, error } = await supabase
    .from('i18n_batch')
    .select(
      'id, member_id, note, submitted_at,' +
      ' member:i18n_member(display_name, avatar_url),' +
      ' items:i18n_batch_item(id, unit_id, locale, kind, from_text, to_text, note, decision)',
    )
    .eq('state', 'submitted')
    .order('submitted_at', { ascending: true });
  if (error) throw error;
  // PostgREST returns an embedded to-one relation as an object but types it as
  // an array (and as an error shape when it cannot infer the join); accept
  // whichever arrives rather than trusting the generated type.
  type Raw = Omit<SubmittedBatch, 'member'> & {
    member: SubmittedBatch['member'] | SubmittedBatch['member'][];
  };
  return ((data ?? []) as unknown as Raw[]).map((b) => ({
    ...b,
    member: Array.isArray(b.member) ? (b.member[0] ?? null) : b.member,
  }));
}

/** The English each submitted item is a translation of, for side-by-side review. */
export async function fetchSources(unitIds: string[]): Promise<Map<string, string>> {
  if (!unitIds.length) return new Map();
  const { data, error } = await supabase
    .from('i18n_unit').select('unit_id, source_text').in('unit_id', unitIds);
  if (error) throw error;
  return new Map((data ?? []).map((u) => [u.unit_id as string, u.source_text as string]));
}

/** Owner only, enforced in the database. Approving a "looks good" also records
 *  the sign-off on the translation itself. */
export async function decideItems(ids: string[], decision: 'approved' | 'rejected') {
  const { error } = await supabase.rpc('i18n_decide_items', {
    p_item_ids: ids, p_decision: decision,
  });
  if (error) throw error;
}

/** Approved text changes that no pull request has carried yet — exactly what
 *  the Open pull request button would ship. */
export async function fetchShippable(): Promise<BatchItem[]> {
  const { data, error } = await supabase
    .from('i18n_batch_item')
    .select('id, unit_id, locale, kind, from_text, to_text, note, decision, ' +
            'unit:i18n_unit(retired_at), pr:i18n_batch_item_pr(pr_id)')
    .eq('decision', 'approved')
    .eq('kind', 'fix');
  if (error) throw error;
  const one = <T,>(v: T | T[] | null): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);
  return ((data ?? []) as unknown as
      (BatchItem & { pr: unknown[]; unit: { retired_at: string | null } | { retired_at: string | null }[] })[])
    // No pull request row yet means it has not shipped.
    .filter((i) => !i.pr?.length)
    // A fix for a string that has left the app can never ship: the key is gone
    // from the catalog, so the pull request builder sets it aside. Counting it
    // here would leave the queue permanently showing work nobody can do.
    .filter((i) => !one(i.unit)?.retired_at);
}

/** Items a reviewer has sent that the owner has not decided on yet. */
export async function countAwaitingDecision(): Promise<number> {
  const { count, error } = await supabase
    .from('i18n_batch_item')
    .select('id', { count: 'exact', head: true })
    .eq('decision', 'pending');
  if (error) throw error;
  return count ?? 0;
}

export interface PrResult {
  number: number; url: string; files: string[]; items: number;
}

/** Turns every approved-but-unshipped fix into one pull request. */
export async function createPullRequest(batchIds?: string[]): Promise<PrResult> {
  const { data, error } = await supabase.functions.invoke('translations-pr', {
    body: batchIds?.length ? { batchIds } : {},
  });
  if (error) {
    // The function reports why in the body; surface that rather than
    // "Edge Function returned a non-2xx status code".
    const detail = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(detail?.error
      ? `${detail.error}${detail.problems ? `\n- ${detail.problems.join('\n- ')}` : ''}${detail.detail ? `\n${detail.detail}` : ''}`
      : error.message);
  }
  return data as PrResult;
}

/** The name everyone else sees. The workspace keeps its own copy rather than
 *  following the Google account, so this is the only thing that changes it. */
export async function setMyName(name: string): Promise<Member> {
  const { data, error } = await supabase.rpc('i18n_set_my_name', { p_name: name });
  if (error) throw error;
  return data as Member;
}

export async function setMyLanguages(languages: string[]) {
  const { error } = await supabase.rpc('i18n_set_my_languages', { p_languages: languages });
  if (error) throw error;
}

/* ---------------------------------------------------------------- notes */

/** Both halves of a deck prompt share one conversation, so both must reduce to
 *  the same key. Mirrors i18n_thread_units on the database side. */
export function threadKey(unitId: string, locale: string) {
  const [kind, promptId] = unitId.split('/');
  return kind === 'deck' && promptId ? `deck/${promptId}@${locale}` : `${unitId}@${locale}`;
}

/** The conversation on a set of strings, authors resolved. Empty list in,
 *  empty list out — the RPC is skipped rather than asked about nothing. */
export async function fetchNotes(unitIds: string[], locales?: string[]): Promise<Note[]> {
  const ids = [...new Set(unitIds)];
  if (!ids.length) return [];
  const { data, error } = await supabase.rpc('i18n_notes', {
    p_unit_ids: ids, p_locales: locales?.length ? locales : null,
  });
  if (error) throw error;
  return (data ?? []) as Note[];
}

/**
 * Post into a thread. Unlike a fix this is not batched: a reply is a
 * conversation, and holding it in a cart until the reviewer remembers to
 * submit is how questions go stale.
 *
 * An answer takes the other route so it also closes whatever the owner still
 * had queued for that string.
 */
export async function postNote(
  unitId: string, locale: string, body: string, kind: NoteKind,
) {
  const { error } = kind === 'answer'
    ? await supabase.rpc('i18n_answer_question', {
        p_unit_id: unitId, p_locale: locale, p_body: body,
      })
    : await supabase.rpc('i18n_post_note', {
        p_unit_id: unitId, p_locale: locale, p_body: body, p_kind: kind,
      });
  if (error) throw error;
}

/** Threads whose newest note is somebody else's — what is waiting on you. */
export async function fetchOpenThreads(locales?: string[]): Promise<OpenThread[]> {
  const { data, error } = await supabase.rpc('i18n_open_threads', {
    p_locales: locales?.length ? locales : null,
  });
  if (error) throw error;
  return (data ?? []) as OpenThread[];
}

/** Standings for everyone with work in the system. Small enough to fetch whole
 *  and rank on the client, which is what lets a reviewer outside the visible
 *  top still be shown their own place. */
export async function fetchLeaderboard(): Promise<LeaderRow[]> {
  const { data, error } = await supabase.rpc('i18n_leaderboard', { p_limit: 50 });
  if (error) throw error;
  return ((data ?? []) as LeaderRow[]).map((r) => ({
    ...r,
    cards: Number(r.cards), fixes: Number(r.fixes),
    shipped: Number(r.shipped), waiting: Number(r.waiting),
  }));
}

/* ---------------------------------------------------------------- vibes */

/** Counts and means for a set of prompts, plus your own vote on each. Never
 *  who voted what — that stays behind the owner's policy. */
export async function fetchVibes(promptIds: string[], locales: string[]): Promise<CardVibe[]> {
  if (!promptIds.length) return [];
  const { data, error } = await supabase.rpc('i18n_card_vibes', {
    p_prompt_ids: promptIds, p_locales: locales.length ? locales : null,
  });
  if (error) throw error;
  return ((data ?? []) as CardVibe[]).map((v) => ({
    ...v, votes: Number(v.votes), average: Number(v.average),
    mine: v.mine === null ? null : Number(v.mine),
  }));
}

/** Records a verdict on the card itself. Not batched: it is an opinion about
 *  the game, not a change to the repo, so there is nothing for the owner to
 *  approve and nothing to hold back. Rating again replaces your last one. */
export async function rateCard(promptId: string, locale: string, score: number) {
  const { error } = await supabase.rpc('i18n_rate_card', {
    p_prompt_id: promptId, p_locale: locale, p_score: score,
  });
  if (error) throw error;
}

/* ------------------------------------------------------------ analytics */

/**
 * Distinct people in the app today, by way of an Edge Function.
 *
 * Not a direct PostHog call: reading from PostHog needs a personal API key,
 * which can read the whole project and therefore cannot be in a bundle served
 * to a browser. Throws when it is unconfigured, which the caller treats as
 * "show nothing".
 */
export async function fetchPlayersToday(): Promise<number> {
  const { data, error } = await supabase.functions.invoke('posthog-stats', { body: {} });
  if (error) throw error;
  const users = (data as { users?: unknown })?.users;
  if (typeof users !== 'number') throw new Error('no count returned');
  return users;
}
